'use client';

import { FC } from 'react';
import { Icon, IconTypes, IconSizeProp } from '@/react/components/core-ui/Icon';
import { Drawer } from '@/react/components/core-ui/Drawer';
import { useBrandLocaleValue } from '@/react/hooks/useBrandLocaleValue';
import { classes } from '@/next-utils/css-utils/scss-utils';
import {
  useDrawer,
  useDrawerActions
} from '@/react/components/core-ui/Drawer/hooks';
import { msg } from '@/services/isomorphic/I18NService';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { useLocale } from '@/react/hooks/useLocale';
import { NavigationBack } from '../NavigationBack';
import type { IMobileDrawerProps } from './IMobileDrawerProps';
import { MobileNavItem } from '../MobileNavItem';

import S from '../../styles.base.module.scss';
import { header_navigation_close } from "@/lang/__generated__/ahnu/header_navigation_close";

/**
 * A wrapper component for the typical layout for a mobile drawer (includes a back button, icon, and label).
 *
 * **Note:** This component is intended to be used within the @see Mobile drawer component.
 * @param children - The children to render in the drawer.
 * @param label - The label to display in the drawer.
 * @param icon - The icon to display in the drawer (next to the label).
 * @param labelClassName - The class name to apply to the label.
 */
export const MobileDrawer: FC<IMobileDrawerProps> = ({
  children,
  label,
  icon,
  labelClassName,
  backLabel
}) => {
  const { isOpen, openDrawer, closeDrawer } = useDrawer();
  const { closeAllDrawers } = useDrawerActions();
  const { language, country } = useLocale();

  const menuConfig = ConfigurationService.getConfig('menu', language, country);
  const showCloseButton = menuConfig.getSetting(
    'mobileMenu.showCloseButton'
  ).value;
  const showNavArrow = menuConfig.getSetting('mobileMenu.showNavArrow').value;

  const navArrowIconSize = useBrandLocaleValue<IconSizeProp>(
    () => ({ default: IconSizeProp.SizeSM }),
    []
  );

  const navLabelIconSize = useBrandLocaleValue<IconSizeProp>(
    () => ({ default: IconSizeProp.SizeLG }),
    []
  );

  return (
    <MobileNavItem>
      <button
        type="button"
        className={classes(S.navLink, labelClassName)}
        onClick={openDrawer}
        aria-label={label}
      >
        <span className={S.labelContainer}>
          {icon != null && <Icon icon={icon} size={navLabelIconSize} />}
          {label}
        </span>
        {showNavArrow && (
          <span className={S.navIcon}>
            <Icon icon={IconTypes.AngleRight} size={navArrowIconSize} />
          </span>
        )}
      </button>
      <Drawer
        rootClassName={S.innerDrawerRoot}
        drawerClassName={S.innerDrawer}
        curtainClassName={S.innerDrawerCurtain}
        open={isOpen}
        fromLeft
        usePortal={false}
      >
        {showCloseButton ? (
          <>
            <div className={S.closeButtonRow}>
              <button
                type="button"
                className={S.closeButton}
                onClick={closeAllDrawers}
                aria-label={msg(header_navigation_close)}
              >
                <Icon icon={IconTypes.Close} size={IconSizeProp.SizeLG} />
              </button>
            </div>
            <NavigationBack onClick={closeDrawer} label={backLabel} />
          </>
        ) : (
          <NavigationBack onClick={closeDrawer} label={backLabel} />
        )}
        {children}
      </Drawer>
    </MobileNavItem>
  );
};
